
import { format } from "date-fns";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarEvent } from "@/types/calendar";
import { useAddEvent } from "@/hooks/useEvents";
import { toast } from "sonner";
import { EventSchema, EventFormValues } from "../schemas/eventSchema";

export const useEventForm = (selectedDate: Date, onClose: () => void) => {
  const { mutate: addEvent, isPending } = useAddEvent();

  const form = useForm<EventFormValues>({
    resolver: zodResolver(EventSchema),
    defaultValues: {
      title: "",
      description: "",
      date: selectedDate,
      startTime: format(new Date().setHours(9, 0, 0, 0), "HH:mm"),
      endTime: format(new Date().setHours(10, 0, 0, 0), "HH:mm"),
      location: "",
      type: "other",
      attendees: "",
    },
  });

  const onSubmit = (data: EventFormValues) => {
    console.log('Form submitted with data:', data);
    
    const { date, startTime, endTime, attendees, ...rest } = data;
    
    // Convert the date and times to Date objects
    const [startHour, startMinute] = (startTime as string).split(":").map(Number);
    const [endHour, endMinute] = (endTime as string).split(":").map(Number);
    
    const startDate = new Date(date as Date);
    startDate.setHours(startHour, startMinute, 0, 0);
    
    const endDate = new Date(date as Date);
    endDate.setHours(endHour, endMinute, 0, 0);
    
    // Validate that end time is after start time
    if (endDate <= startDate) {
      toast.error("End time must be after start time");
      return;
    }
    
    // Process attendees
    const attendeesList = (attendees as string)
      ? (attendees as string).split(",").map(a => a.trim()).filter(Boolean)
      : [];
    
    // Ensure we're matching the required type structure
    const newEvent: Omit<CalendarEvent, 'id' | 'user_id'> = {
      title: rest.title as string,
      description: rest.description as string,
      location: rest.location as string,
      type: rest.type as CalendarEvent['type'],
      start_time: startDate.toISOString(),
      end_time: endDate.toISOString(),
      attendees: attendeesList,
      status: 'confirmed',
      source: 'manual'
    };
    
    console.log('Creating event:', newEvent);
    
    addEvent(newEvent, {
      onSuccess: (createdEvent) => {
        console.log('Event created successfully:', createdEvent);
        toast.success("Event created successfully!");
        form.reset();
        onClose();
      },
      onError: (error) => {
        console.error('Error creating event:', error);
        toast.error("Failed to create event. Please try again.");
      },
    });
  };

  return {
    form,
    isPending,
    onSubmit: form.handleSubmit(onSubmit),
  };
};
